import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Upload, MapPin, X } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";

export default function PostTask() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const hireLaborerId = params.get("helper");
  const [hireLaborerInfo, setHireLaborerInfo] = useState(null);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", category: "", budget: "", location: "",
    scheduled_date: "", skills_required: "", photo_url: "",
    latitude: null, longitude: null,
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)); }, []);

  useEffect(() => {
    if (hireLaborerId) {
      api.get(`/laborers/${hireLaborerId}`).then(({ data }) => setHireLaborerInfo(data.laborer)).catch(() => {});
    }
  }, [hireLaborerId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.file_id}`;
      setForm((f) => ({ ...f, photo_url: url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
        toast.success("Location pinned");
      },
      () => toast.error("Could not get location"),
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        budget: parseFloat(form.budget),
        skills_required: form.skills_required.split(",").map(s => s.trim()).filter(Boolean),
        hire_laborer_id: hireLaborerId || undefined,
      };
      const { data } = await api.post("/tasks", payload);
      toast.success(hireLaborerId ? "Hired! Task created & assigned." : "Task posted!");
      navigate(`/tasks/${data.task_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
        {hireLaborerId ? "Hire directly" : "Post a task"}
      </h1>
      <p className="text-slate-600 mt-1">
        {hireLaborerInfo
          ? <>You're hiring <b>{hireLaborerInfo.name}</b> directly — they'll be assigned to this task.</>
          : "Describe what needs doing. Helpers nearby will apply."}
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-5 bg-white rounded-2xl border border-slate-200/80 p-6">
        <div>
          <Label htmlFor="title">Task title</Label>
          <Input id="title" required value={form.title} onChange={set("title")} placeholder="e.g., Help me move a couch" className="mt-1.5 h-11" data-testid="post-title-input" />
        </div>
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea id="description" required value={form.description} onChange={set("description")} rows={4} className="mt-1.5" data-testid="post-description-input" />
        </div>

        {/* Photo */}
        <div>
          <Label>Task photo (optional)</Label>
          {form.photo_url ? (
            <div className="mt-1.5 relative w-full max-w-sm">
              <img src={form.photo_url} alt="Task" className="w-full h-48 object-cover rounded-xl border border-slate-200" />
              <button type="button" onClick={() => setForm((f) => ({ ...f, photo_url: "" }))} className="absolute top-2 right-2 bg-white/95 rounded-full p-1.5 shadow" data-testid="remove-photo-btn">
                <X size={14} />
              </button>
            </div>
          ) : (
            <label className="mt-1.5 inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-300 hover:bg-slate-50 cursor-pointer text-sm font-semibold">
              <Upload size={14} /> {uploading ? "Uploading…" : "Attach a photo"}
              <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="task-photo-input" />
            </label>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger className="h-11 mt-1.5" data-testid="post-category-select"><SelectValue placeholder="Pick a category" /></SelectTrigger>
              <SelectContent>
                {cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="budget">Budget (USD)</Label>
            <Input id="budget" required type="number" min="1" step="1" value={form.budget} onChange={set("budget")} className="mt-1.5 h-11" data-testid="post-budget-input" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="location">Location</Label>
            <div className="flex flex-col sm:flex-row gap-2 mt-1.5 items-stretch sm:items-start">
              <div className="flex-1">
                <AddressAutocomplete
                  value={form.location}
                  onChange={(v) => setForm((f) => ({ ...f, location: v }))}
                  onSelect={({ display_name, latitude, longitude }) => setForm((f) => ({ ...f, location: display_name, latitude, longitude }))}
                  placeholder="Start typing an address in Moscow, ID…"
                  testid="post-location-input"
                />
              </div>
              <Button type="button" variant="outline" onClick={useMyLocation} data-testid="use-location-btn" className="rounded-full whitespace-nowrap h-11 justify-center">
                <MapPin size={14} className="mr-1" /> {form.latitude ? "Pinned" : "Pin me"}
              </Button>
            </div>
            {form.latitude && <div className="text-xs text-slate-500 mt-1 font-mono-money">📍 {form.latitude.toFixed(4)}, {form.longitude.toFixed(4)}</div>}
          </div>
          <div>
            <Label htmlFor="scheduled_date">When?</Label>
            <Input id="scheduled_date" value={form.scheduled_date} onChange={set("scheduled_date")} placeholder="e.g., Sat afternoon" className="mt-1.5 h-11" data-testid="post-when-input" />
          </div>
          <div>
            <Label htmlFor="skills">Skills (comma-separated)</Label>
            <Input id="skills" value={form.skills_required} onChange={set("skills_required")} placeholder="lifting, ladder, drill" className="mt-1.5 h-11" data-testid="post-skills-input" />
          </div>
        </div>
        <Button type="submit" disabled={loading} data-testid="post-submit-btn" className="w-full h-11 bg-[#D9FF3D] hover:bg-[#C6EB2D] text-[#23272B] font-bold rounded-full btn-primary-glow">
          {loading ? "Posting…" : hireLaborerId ? `Hire ${hireLaborerInfo?.name || "helper"} now` : "Post task"}
        </Button>
      </form>
    </div>
  );
}
